import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Search } from "lucide-react";
import { SeasonSelect } from "@/components/materials/SeasonSelect";
import { SheetCard } from "@/components/modelsheets/SheetCard";
import { SheetEditor } from "@/components/modelsheets/SheetEditor";
import { blankSheet, byCode, sheetFabrics } from "@/lib/modelSheets";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CutTicketsSection } from "@/components/cuttickets/CutTicketsSection";
import { SimulationsSection } from "@/components/planning/SimulationsSection";
import { BomsSection } from "@/components/boms/BomsSection";

export default function Modellistica() {
  const { data: seasons } = useQuery({ queryKey: ["model-sheet-seasons"], queryFn: async () => (await api.get("/model-sheets/seasons")).data });
  const [pick, setPick] = useState(null);
  const season = pick || seasons?.current;
  const { data: sheets = [], isLoading } = useQuery({
    queryKey: ["model-sheets", season], enabled: !!season,
    queryFn: async () => (await api.get("/model-sheets", { params: { season } })).data,
  });
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("distinte");
  const shown = useMemo(() => {
    const w = search.toLowerCase().split(/\s+/).filter(Boolean);
    return sheets.filter((s) => w.every((x) => [s.name, s.code, ...sheetFabrics(s).map((f) => `${f.fabric_type} ${f.name} ${f.color}`)].join(" ").toLowerCase().includes(x))).sort(byCode);
  }, [sheets, search]);
  const newSeason = season && season !== "tutti" ? season : seasons?.current;

  return (
    <div data-testid="modellistica-page">
      <PageHeader title="Modellistica" subtitle="Distinta base, pianificazione dei pezzi da produrre, scheda tecnica e scheda taglio, divise per stagione." />
      <Tabs value={view} onValueChange={setView} className="mb-4">
        <TabsList className="max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="distinte" data-testid="modellistica-tab-distinte">1 · Distinte base</TabsTrigger>
          <TabsTrigger value="simulazione" data-testid="modellistica-tab-simulazione">2 · Pianificazione produzione</TabsTrigger>
          <TabsTrigger value="tecniche" data-testid="modellistica-tab-tecniche">3 · Schede tecniche</TabsTrigger>
          <TabsTrigger value="taglio" data-testid="modellistica-tab-taglio">4 · Schede taglio</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:max-w-sm sm:flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" placeholder={view === "tecniche" ? "Cerca modello, codice, tessuto..." : view === "taglio" ? "Cerca modello, codice, colore..." : view === "distinte" ? "Cerca codice, articolo, colore..." : "Cerca simulazione o articolo..."} data-testid="sheets-search" />
        </div>
        {seasons && <SeasonSelect value={season} options={seasons.options} current={seasons.current} onChange={setPick} testid="sheets-season-select" />}
        {view === "tecniche" && <Button className="sm:ml-auto" onClick={() => setEditing(blankSheet(newSeason))} disabled={!newSeason} data-testid="new-sheet-btn"><Plus className="mr-2 h-4 w-4" />Nuova scheda tecnica</Button>}
      </div>
      {view === "distinte" && season && <BomsSection season={season} newSeason={newSeason} search={search} />}
      {view === "taglio" && season && <CutTicketsSection season={season} sheets={sheets} search={search} />}
      {view === "simulazione" && season && <SimulationsSection season={season} sheets={sheets} search={search} />}
      {view === "tecniche" && <>
      {(isLoading || !season) && <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}
      {!isLoading && season && shown.length === 0 && (
        <p className="py-16 text-center text-sm text-muted-foreground" data-testid="sheets-empty">
          {sheets.length ? "Nessuna scheda corrisponde alla ricerca." : `Nessuna scheda per ${season === "tutti" ? "nessuna stagione" : `la stagione ${season}`}: crea la prima con "Nuova scheda tecnica".`}
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {shown.map((s) => <SheetCard key={s.id} sheet={s} onOpen={() => setEditing(s)} showSeason={season === "tutti"} />)}
      </div>
      </>}
      {editing && <SheetEditor sheet={editing} seasons={seasons?.options || []} onClose={() => setEditing(null)} onSaved={setEditing} />}
    </div>
  );
}
